import React from 'react';
import { ClientIdContext } from 'bk-web-app';
import { Query } from '@apollo/client/react/components';
import snakeCase from 'lodash/snakeCase';
import { Button } from 'bench-internal-theme';
import { downloadCSV } from 'csv-functions';

import { compareTransactionDates } from './comparators';
import { GET_CLIENT_COMPANY } from './queries';
import type { DataEntry, DateRange, LedgerAccount } from './types';

type LedgerAccountCSVExportContainerProps = {
  dateRange: DateRange;
  dataEntriesToExport: Array<DataEntry>;
  ledgerAccount: LedgerAccount;
};

const LedgerAccountCSVExportContainer: React.FC<LedgerAccountCSVExportContainerProps> = ({
  children,
  dataEntriesToExport,
  dateRange,
  ledgerAccount,
}) => (
  <ClientIdContext.Consumer>
    {clientId => (
      <Query query={GET_CLIENT_COMPANY} variables={{ id: clientId }}>
        {({ data, loading, error }: any) => {
          if (loading || error) {
            return null;
          }

          const { startDate, endDate } = dateRange;
          const { company } = data && data.client;
          const csvFileName = `transactions_${snakeCase(ledgerAccount.name)}_${endDate || ''}`;
          const trimXssChars = x => {
            if (typeof x === 'string') {
              return x.replace(/^( *)([=+\-@]+)/g, '‘$2');
            }
            return x;
          };

          const handleCSVDownload = () => {
            const csvHeaderTitle = `Custom "${ledgerAccount.name}" report for ${company} for ${
              startDate || ''
            } to ${endDate || ''}`;
            const csvHeaderColumns = [
              'Transaction Date',
              'Transaction Description',
              'Data Entry Amount',
              'Linked add-on name',
            ];
            const dataEntriesCSVFormatted = [
              [csvHeaderTitle, undefined, undefined, undefined],
              [undefined, undefined, undefined, undefined],
              csvHeaderColumns,
            ];
            dataEntriesToExport.sort(compareTransactionDates);
            dataEntriesToExport.forEach(dataEntry => {
              dataEntriesCSVFormatted.push([
                trimXssChars(dataEntry.journalEntry.dateOfTransaction),
                trimXssChars(dataEntry.journalEntry.vendorName),
                (dataEntry.amountInCents / 100).toFixed(2),
                dataEntry.journalEntry.addOn && trimXssChars(dataEntry.journalEntry.addOn.name),
              ]);
            });

            downloadCSV(csvFileName, dataEntriesCSVFormatted);
          };

          return (
            <Button onClick={handleCSVDownload} size="xsmall" styleType="total-ghost">
              {children}
            </Button>
          );
        }}
      </Query>
    )}
  </ClientIdContext.Consumer>
);

export default LedgerAccountCSVExportContainer;
